<template>
<div class="card-form">
  <div class="card-list">
    <Card :fields="fields" :labels="formData" :isCardNumberMasked="isCardNumberMasked" :randomBackgrounds="randomBackgrounds" :backgroundImage="backgroundImage" />
  </div>
  <div class="card-form__inner">
    <div class="card-input">
      <label for="cardNumber" class="card-input__label">Card Number</label>
      <input type="tel" :id="fields.cardNumber" @input="changeNumber" @focus="focusCardNumber" @blur="blurCardNumber" class="card-input__input" :value="formData.cardNumber" :maxlength="cardNumberMaxLength" data-card-field autocomplete="off" />
      <button class="card-input__eye" :class="{ '-active' : !isCardNumberMasked }" title="Show/Hide card number" tabindex="-1" :disabled="formData.cardNumber === ''" @click="toggleMask"></button>
    </div>
    <div class="card-input">
      <label for="cardName" class="card-input__label">Card Name</label>
      <input type="text" :id="fields.cardName" v-letter-only @input="changeName" class="card-input__input" :value="formData.cardName" data-card-field autocomplete="off" />
    </div>
    <div class="card-form__row">
      <div class="card-form__col">
        <div class="card-form__group">
          <label for="cardMonth" class="card-input__label">Expiration Date</label>
          <select class="card-input__input -select" :id="fields.cardMonth" v-model="formData.cardMonth" @change="changeMonth" data-card-field>
            <option value disabled selected>Month</option>
            <option v-bind:value="n < 10 ? '0' + n : n" v-for="n in 12" v-bind:disabled="n < minCardMonth" v-bind:key="n">{{generateMonthValue(n)}}</option>
          </select>
          <select class="card-input__input -select" :id="fields.cardYear" v-model="formData.cardYear" @change="changeYear" data-card-field>
            <option value disabled selected>Year</option>
            <option v-bind:value="$index + minCardYear" v-for="(n, $index) in 12" v-bind:key="n">{{$index + minCardYear}}</option>
          </select>
        </div>
      </div>
      <div class="card-form__col -cvv">
        <div class="card-input">
          <label for="cardCvv" class="card-input__label">CVV</label>
          <input type="tel" class="card-input__input" v-number-only :id="fields.cardCvv" maxlength="4" :value="formData.cardCvv" @input="changeCvv" data-card-field autocomplete="off" />
        </div>
      </div>
    </div>

    <div style="margin-bottom: 5px;" v-if="haserrors" class="error">Please make sure you've
      entered a valid card and try again.</div>
    <button class="card-form__button" style="background-color: black" v-on:click="handleSubmit">{{submitText}}</button>
    <button class="card-form__button" style="background-color: black" v-on:click="$router.push('/signupcomplete')">Add Later</button>
  </div>
</div>
</template>

<script>
import Card from '@/components/Card'
import config from '@/config'
var stripe = Stripe(config.stripeKey)
import validator from 'validator'
import creditCardType from 'credit-card-type'
import { setupService } from '@/services/setup'

export default {
  name: 'CardForm',
  directives: {
    'number-only': {
      bind(el) {
        function checkValue(event) {
          event.target.value = event.target.value.replace(/[^0-9]/g, '')
          if (event.charCode >= 48 && event.charCode <= 57) {
            return true
          }
          event.preventDefault()
        }
        el.addEventListener('keypress', checkValue)
      }
    },
    'letter-only': {
      bind(el) {
        function checkValue(event) {
          if (event.charCode >= 48 && event.charCode <= 57) {
            event.preventDefault()
          }
          return true
        }
        el.addEventListener('keypress', checkValue)
      }
    }
  },
  props: {
    formData: {
      type: Object,
      default: () => {
        return {
          cardName: '',
          cardNumber: '',
          cardMonth: '',
          cardYear: '',
          cardCvv: ''
        }
      }
    },
    callBack: {
      type: Function
    },
    backgroundImage: [String, Object],
    randomBackgrounds: {
      type: Boolean,
      default: true
    }
  },
  components: {
    Card
  },
  data() {
    return {
      submitText: 'Submit',
      haserrors: false,
      fields: {
        cardNumber: 'v-card-number',
        cardName: 'v-card-name',
        cardMonth: 'v-card-month',
        cardYear: 'v-card-year',
        cardCvv: 'v-card-cvv'
      },
      minCardYear: new Date().getFullYear(),
      isCardNumberMasked: true,
      mainCardNumber: this.cardNumber,
      cardNumberMaxLength: 19
    }
  },
  computed: {
    zip() {
      let zip
      if (this.$store.state.setup.address.zip) {
        zip = this.$store.state.setup.address.zip

      }
      if (this.$store.state.auth.user.info.billingAddress) {
        zip = this.$store.state.auth.user.info.billingAddress.postal_code
      }
      return zip
    },
    cardType() {
      if (!this.formData.cardNumber) return null
      let numString = this.formData.cardNumber.toString()
      let cards = creditCardType(numString)
      if (cards && cards[0].type) return cards[0].type
      return cards
    },
    minCardMonth() {
      if (this.formData.cardYear === this.minCardYear) return new Date().getMonth() + 1
      return 1
    }
  },
  watch: {
    cardYear() {
      if (this.formData.cardMonth < this.minCardMonth) {
        this.formData.cardMonth = ''
      }
    }
  },
  mounted() {
    this.maskCardNumber()
  },
  methods: {
    /* our submit methods */
    async createToken(card) {
      let cardData = {
        card: {
          name: card.name,
          number: card.number,
          exp_month: card.month,
          exp_year: card.year,
          cvc: card.code,
          address_zip: this.zip
        },
      }
      console.log(`creating stripe token with data`, cardData)
      let token = await setupService.getStripeToken(cardData)
      // console.log(`got token`, token)
      return token
    },
    validateForm() {
      let { cardNumber, cardYear, cardMonth, cardName, cardCvv } = this.formData
      let { cardType } = this
      let validNumber = validator.isCreditCard(cardNumber)
      let isPrivacy = cardNumber.toString().startsWith('4767') || cardNumber.toString().startsWith('4859')
      if(isPrivacy) return false
      let codeStr = cardCvv.toString()
      // console.log(codeStr,validNumber,month,year,name)
      if (codeStr.length != 3 && codeStr.length != 4) return false
      // consolle.log(codeStr,validNumber)
      if (!cardYear || !cardMonth || !cardCvv || !cardName || !validNumber) return false
      return true
    },
    async handleSubmit(e) {
      this.submitText = 'Adding Card...'
      console.log('form submitted')
      // this.haserrors = false

      let { cardNumber, cardYear, cardMonth, cardName, cardCvv } = this.formData
      let { cardType } = this
      // console.log(month, year, number, code, name, cardType)
      let validated = this.validateForm()
      if (!cardMonth || !cardYear || !cardNumber || !cardCvv || !cardName || !cardType || !validated) {
        this.haserrors = true
        return console.error('form incomplete')
      }
      var re = new RegExp(' ', 'g');
      let card = {
        month: cardMonth,
        year: cardYear,
        number: cardNumber.replace(re, ''),
        code: cardCvv,
        name: cardName,
        cardType: cardType
      }
      console.log(card)
      console.log(`creating stripe card token`)
      try {
        let token = await this.createToken(card)
        if (token && token.id) {
          console.log(`created stripe token!`, token)
          let payload = {
            card,
            token: token.id
          }
          console.log(`dispatching to callBack`)
          this.callBack(payload)
          // this.$store.dispatch('setup/step3', payload)
        }
      } catch (e) {
        console.error(e)
        this.haserrors = true
      }

    },
    /* ./ our submit methods */

    generateMonthValue(n) {
      return n < 10 ? `0${n}` : n
    },
    changeName(e) {
      this.formData.cardName = e.target.value
      this.$emit('input-card-name', this.formData.cardName)
    },
    changeNumber(e) {
      this.formData.cardNumber = e.target.value
      let value = this.formData.cardNumber.replace(/\D/g, '')
      // american express, 15 digits
      if ((/^3[47]\d{0,13}$/).test(value)) {
        this.formData.cardNumber = value.replace(/(\d{4})/, '$1 ').replace(/(\d{4}) (\d{6})/, '$1 $2 ')
        this.cardNumberMaxLength = 17
      } else if ((/^3(?:0[0-5]|[68]\d)\d{0,11}$/).test(value)) { // diner's club, 14 digits
        this.formData.cardNumber = value.replace(/(\d{4})/, '$1 ').replace(/(\d{4}) (\d{6})/, '$1 $2 ')
        this.cardNumberMaxLength = 16
      } else if ((/^\d{0,16}$/).test(value)) { // regular cc number, 16 digits
        this.formData.cardNumber = value.replace(/(\d{4})/, '$1 ').replace(/(\d{4}) (\d{4})/, '$1 $2 ').replace(/(\d{4}) (\d{4}) (\d{4})/, '$1 $2 $3 ')
        this.cardNumberMaxLength = 19
      }
      this.$emit('input-card-number', this.formData.cardNumber)
    },
    changeMonth() {
      this.$emit('input-card-month', this.formData.cardMonth)
    },
    changeYear() {
      this.$emit('input-card-year', this.formData.cardYear)
    },
    changeCvv(e) {
      this.formData.cardCvv = e.target.value
      this.$emit('input-card-cvv', this.formData.cardCvv)
    },
    invaildCard() {
      let number = this.formData.cardNumber
      let sum = 0
      let isOdd = true
      for (let i = number.length - 1; i >= 0; i--) {
        let num = number.charAt(i)
        if (isOdd) {
          sum += num
        } else {
          num = num * 2
          if (num > 9) {
            num = num.toString().split('').join('+')
          }
          sum += num
        }
        isOdd = !isOdd
      }
      if (sum % 10 !== 0) {
        alert('invaild card number')
      }
      this.handleSubmit()
    },
    blurCardNumber() {
      if (this.isCardNumberMasked) {
        this.maskCardNumber()
      }
    },
    maskCardNumber() {
      this.mainCardNumber = this.formData.cardNumber
      return
      let arr = this.formData.cardNumber.split('')
      arr.forEach((element, index) => {
        if (index > 4 && index < 14 && element.trim() !== '') {
          arr[index] = '*'
        }
      })
      this.formData.cardNumber = arr.join('')
    },
    unMaskCardNumber() {
      this.formData.cardNumber = this.mainCardNumber
    },
    focusCardNumber() {
      this.unMaskCardNumber()
    },
    toggleMask() {
      this.isCardNumberMasked = !this.isCardNumberMasked
      if (this.isCardNumberMasked) {
        this.maskCardNumber()
      } else {
        this.unMaskCardNumber()
      }
    }
  }
}
</script>
